import { z } from 'zod'

import { useTranslation } from '@flecto/translations'
import { type CoreBundle } from '@flecto/types'

import { type CoreBundleForm } from '../../types/CoreBundleForm'

type UseFormBundleSchemaOptions = {
  bundle?: Partial<CoreBundle>
}

export function useFormBundleSchema(options?: UseFormBundleSchemaOptions) {
  const { t } = useTranslation('saas', { keyPrefix: 'validation' })

  const schema = z.object({
    bundle_items: z.array(
      z.object({
        title: z.string(),
        item_id: z.string({ required_error: t('required') }),
        quantity: z.number({ required_error: t('required') }),
        discount_amount: z.number(),
        discount_type: z.string(),
        _destroy: z.boolean({ required_error: t('required') }),
      })
    ),
    title: z.string().min(2, { message: t('required') }),
    description: z.string().min(8, { message: t('required') }),
    category_id: z.string({ required_error: t('required') }),
    photos_attributes: z.array(
      z.object({
        main: z.boolean({ required_error: t('required') }),
        order: z.number({ required_error: t('required') }),
        photo_image_id: z.string().optional(),
        _destroy: z.boolean({ required_error: t('required') }),
      })
    ),
    on_store: z.boolean({ required_error: t('required') }),
    status: z.boolean({ required_error: t('required') }),
  })

  const initialValues: CoreBundleForm = {
    ...options?.bundle,
    internal_id: options?.bundle?.internal_id ?? undefined,
    bundle_items:
      options?.bundle?.bundle_items?.map((bi) => ({
        title: bi.item.title,
        item_id: bi.item.id,
        id: bi.id,
        quantity: bi.quantity,
        discount_amount: bi.discount_amount ?? 0,
        discount_type: bi.discount_type ?? 'discount_value',
        main_photo: bi.item.main_photo,
        quantity_available: bi.item.quantity_available,
        allow_overbooking: bi.item.allow_overbooking,
        track_quantity: bi.item.track_quantity,
        has_bundles_associated: bi.item.has_bundles_associated,
        owner_address: bi.item.owner_address,
        _destroy: false,
      })) ?? [],
    title: options?.bundle?.title ?? '',
    description: options?.bundle?.description ?? '',
    category_id: String(options?.bundle?.category_id ?? ''),
    photos_attributes: [],
    on_store: options?.bundle?.on_store ?? true,
    status: options?.bundle?.status ?? 'enabled',
  }

  return { schema, initialValues }
}
